import { DateTime, DateTimeUnit } from 'luxon';
import { action, computed, makeObservable } from 'mobx';

import { ViewTable } from '@/configs/ViewTable';
import { ValueModel } from '@/models/ValueModel';
import { formatWeekInterval } from '@/utils/formatWeekInterval';

interface DateNavigationDependencies {
  getView: () => ViewTable;
  setView: (view: ViewTable) => void;
}

/**
 * Модель навигации по датам: текущая дата, переключение периодов, заголовок.
 */
export class DateNavigationModel {
  private readonly _currentDate: ValueModel<DateTime>;
  private readonly _deps: DateNavigationDependencies;

  constructor(initialDate: DateTime, deps: DateNavigationDependencies) {
    this._currentDate = new ValueModel<DateTime>(initialDate);
    this._deps = deps;

    makeObservable(this, {
      currentDate: computed,
      view: computed,
      title: computed,
      isCurrentPeriod: computed,
      setCurrentDate: action.bound,
      setView: action.bound,
      nextPeriod: action.bound,
      prevPeriod: action.bound,
      goToToday: action.bound,
    });
  }

  get currentDate(): DateTime {
    return this._currentDate.value;
  }

  get view(): ViewTable {
    return this._deps.getView();
  }

  get title(): string {
    const viewUnit = this.view as DateTimeUnit;

    if (viewUnit === 'week') {
      const start = this.currentDate.startOf('week').toISODate();
      const end = this.currentDate.startOf('week').plus({ days: 6 }).toISODate();

      if (!start || !end) return '';

      return formatWeekInterval(start, end);
    }

    return this.currentDate
      .setLocale('ru')
      .toFormat('LLLL yyyy')
      .replace(/^./, (c) => c.toUpperCase());
  }

  setCurrentDate(date: DateTime): void {
    this._currentDate.change(date);
  }

  // Если просматривается текущий месяц, переключение на неделю отправит на сегодня
  // Иначе покажется первый день месяца
  setView(view: ViewTable): void {
    const oldViewUnit = this.view as DateTimeUnit;
    const today = DateTime.local();

    const isCurrentPeriod = this.currentDate.hasSame(today, oldViewUnit);

    this._deps.setView(view);
    const viewUnit = view as DateTimeUnit;
    const targetDate = isCurrentPeriod ? today : this.currentDate;

    this.setCurrentDate(targetDate.startOf(viewUnit));
  }

  get isCurrentPeriod(): boolean {
    const today = DateTime.local();
    const viewUnit = this.view as DateTimeUnit;

    return this.currentDate.hasSame(today, viewUnit);
  }

  goToToday(): void {
    const today = DateTime.local();
    const viewUnit = this.view as DateTimeUnit;

    this.setCurrentDate(today.startOf(viewUnit));
  }

  nextPeriod(): void {
    const viewUnit = this.view as DateTimeUnit;
    const next = this.currentDate.plus({ [`${viewUnit}s`]: 1 }).startOf(viewUnit);
    this.setCurrentDate(next);
  }

  prevPeriod(): void {
    const viewUnit = this.view as DateTimeUnit;
    const prev = this.currentDate.minus({ [`${viewUnit}s`]: 1 }).startOf(viewUnit);
    this.setCurrentDate(prev);
  }
}
